import { Group } from "@visx/group";
import { ParentSize } from "@visx/responsive";
import { ChartArea } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/components/ChartArea";
import { ChartGradient } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/components/ChartGradient";
import { ChartGridLines } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/components/ChartGridLines";
import { ChosenStretchLine } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/components/ChosenStretchLine";
import { DataPoints } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/components/DataPoints";
import { DataPointTimeLabels } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/components/DataPointTimeLabels";
import { MarkerLayer } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/components/markerLayer/MarkerLayer";
import { YAxisLabels } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/components/YAxisLabels";
import { CHART_CONFIG } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/constants";
import {
  useChartScales,
  useChartTooltip,
} from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/hooks";
import { TransportNeedGraphPoint } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/types";
import { getAreaChartMarkers } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/AreaChart/utils";
import { Box, Text, VStack } from "shared/components/Spor";
import { useTheme } from "styled-components";

type AreaChartProps = {
  chosenStretchData: TransportNeedGraphPoint[];
  totalEventData: TransportNeedGraphPoint[];
  maxYValue: number;
  hasResponsiveHeight: boolean;
};

const AreaChartInner = ({
  width,
  height,
  chosenStretchData,
  totalEventData,
  maxYValue,
  hasResponsiveHeight,
}: {
  width: number;
  height: number;
} & AreaChartProps) => {
  const chartHeight = hasResponsiveHeight ? height : CHART_CONFIG.height;
  const xMax = Math.max(
    0,
    width - CHART_CONFIG.margin.left - CHART_CONFIG.margin.right,
  );
  const yMax =
    chartHeight - CHART_CONFIG.margin.top - CHART_CONFIG.margin.bottom;

  const baseData =
    chosenStretchData.length > 0 ? chosenStretchData : totalEventData;

  // Scales
  const { dateScale, valueScale } = useChartScales(
    baseData,
    xMax,
    yMax,
    maxYValue,
  );

  // Tooltip functionality
  const {
    hideTooltip,
    containerRef,
    handleTooltip,
    tooltipData,
    tooltipOpen,
    hoverX,
  } = useChartTooltip(baseData, dateScale, valueScale);

  const dataMarkers = getAreaChartMarkers(chosenStretchData, totalEventData);

  const theme = useTheme();

  return (
    <VStack
      width="100%"
      height="100%"
      minHeight={`${chartHeight + CHART_CONFIG.margin.top + CHART_CONFIG.margin.bottom}px`}
      borderRadius={`${CHART_CONFIG.containerBorderRadius}px`}
      backgroundColor={theme.colorBackgroundTertiary}
    >
      {(chosenStretchData.length > 0 || totalEventData.length > 0) && (
        <svg width={width} height={chartHeight} ref={containerRef}>
          <Group>
            <foreignObject x={18} y={12} width={25} height={21}>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                width="100%"
                height="100%"
              >
                <Text
                  fontWeight="bold"
                  fontSize="2xs"
                  color={theme.colorTextSecondary}
                >
                  Pax
                </Text>
              </Box>
            </foreignObject>
          </Group>
          <Group top={CHART_CONFIG.margin.top}>
            <ChartGradient color={theme.colorOutlineFocus} />

            <YAxisLabels
              valueScale={valueScale}
              desiredTickCount={CHART_CONFIG.desiredTickCount}
            />
            <Group left={CHART_CONFIG.margin.left}>
              <ChartGridLines
                valueScale={valueScale}
                xMax={xMax}
                strokeColor={theme.colorOutlineSecondary}
                desiredTickCount={CHART_CONFIG.desiredTickCount}
              />
              <ChartArea
                data={totalEventData}
                dateScale={dateScale}
                valueScale={valueScale}
              />

              {/* X axis line */}
              <line
                x1={0}
                x2={xMax}
                y1={yMax}
                y2={yMax}
                stroke={theme.colorTextSecondary}
                strokeWidth={CHART_CONFIG.xAxisStrokeWidth}
              />
              <ChosenStretchLine
                data={chosenStretchData}
                dateScale={dateScale}
                valueScale={valueScale}
                strokeColor={theme.colorOutlineFocus}
              />
              <DataPointTimeLabels
                data={baseData}
                dateScale={dateScale}
                yMax={yMax}
              />
              <DataPoints
                data={totalEventData}
                dateScale={dateScale}
                valueScale={valueScale}
              />
              <MarkerLayer
                markers={dataMarkers}
                dateScale={dateScale}
                valueScale={valueScale}
                xMax={xMax}
                yMax={yMax}
                hoverX={tooltipOpen ? hoverX : null}
                hoverData={tooltipOpen ? (tooltipData ?? null) : null}
                points={baseData}
              />

              {/* Invisible hit area for tooltip */}
              <rect
                width={xMax}
                height={yMax}
                fill="transparent"
                onMouseOver={handleTooltip}
                onMouseMove={handleTooltip}
                onMouseLeave={hideTooltip}
              />
            </Group>
          </Group>
        </svg>
      )}
    </VStack>
  );
};

const AreaChart = (props: AreaChartProps) => (
  <ParentSize>
    {({ width, height }) => (
      <AreaChartInner width={width} height={height} {...props} />
    )}
  </ParentSize>
);

export default AreaChart;
